import {createStyles, Group, Stack, Title} from '@mantine/core';
import React, {useCallback} from 'react';

import navigateToURL from 'lib/navigateToURL';
import AgentFoundriesList from 'llm/chat-library/foundries/AgentFoundriesList';
import CreateAgentFoundryEntryPoint from 'llm/chat-library/foundries/CreateAgentFoundryEntryPoint';
import useAgentFoundries from 'llm/chat-library/foundries/useAgentFoundries';
import type {ChatUseCase} from 'llm/chat-library/use-cases/chatUseCaseCatalog';
import type {FoundryUseCaseRunSource} from 'llm/chat-library/use-cases/recordChatUseCaseRun';
import {agentFoundryPageUrl} from 'llm/routes/UrlUtils';
import InlineBanner, {InlineBannerActionType, InlineBannerType} from 'ui-library/banners/InlineBanner';
import {CenteredSpinner} from 'ui-library/Spinner';

const useStyles = createStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 auto',
    minHeight: 0,
    overflowX: 'hidden',
    overflowY: 'auto',
    padding: `${theme.spacing.lg} ${theme.spacing.xxl}`,
  },
}));

interface AgentFoundriesListPageProps {
  onStartFoundryChat: (chatUseCase: ChatUseCase, source: FoundryUseCaseRunSource) => void;
}

/**
 * The Agent Foundries list, rendered as a top-level page in the sidepanel's main content slot (the
 * same slot the chat occupies). Once a foundry exists, the page shows its title, creation menu, and
 * listing. Selecting a row opens the foundry as its own workspace tab (see EditorRoutes.agentFoundryEditor),
 * collapsing this list back to the chat's normal semi-expanded view to make room for it.
 */
export default function AgentFoundriesListPage({
  onStartFoundryChat,
}: AgentFoundriesListPageProps): JSX.Element {
  const {agentFoundries, isLoading, isError, refetch} = useAgentFoundries();
  const {classes} = useStyles();
  const showPageHeader = isLoading || isError || agentFoundries.length > 0;

  const handleSelect = useCallback((agentFoundryId: string) => {
    navigateToURL(agentFoundryPageUrl(agentFoundryId));
  }, []);

  return (
    <Stack spacing="xl" className={classes.container}>
      {showPageHeader && (
        <Group noWrap={true} position="apart" align="center">
          <Title order={3}>Foundries</Title>
          <CreateAgentFoundryEntryPoint onStartFoundryChat={onStartFoundryChat} variant="homepage" />
        </Group>
      )}
      {isError ? (
        <InlineBanner
          bannerType={InlineBannerType.ERROR}
          text="Failed to load foundries."
          action={{
            actionType: InlineBannerActionType.CUSTOM,
            actionText: 'Retry',
            actionOnClick: refetch,
          }}
        />
      ) : isLoading ? (
        <CenteredSpinner />
      ) : (
        <AgentFoundriesList
          agentFoundries={agentFoundries}
          onSelect={handleSelect}
          onStartFoundryChat={onStartFoundryChat}
        />
      )}
    </Stack>
  );
}
